import { v4 as uuidv4 } from "uuid";
import type { Rule, RuleGroup } from "./ConversionRuleBuilder";

type Token =
  | { type: "ident"; value: string }
  | { type: "string"; value: string }
  | { type: "number"; value: number }
  | { type: "keyword"; value: "and" | "or" | "in" | "null" | "true" | "false" }
  | { type: "op"; value: string }
  | { type: "punct"; value: string };

const KEYWORDS = new Set(["and", "or", "in", "null", "true", "false"]);
const TWO_CHAR_OPS = new Set(["==", "!=", ">=", "<=", "=~", "!~"]);

function tokenize(input: string): Token[] {
  const tokens: Token[] = [];
  let i = 0;
  while (i < input.length) {
    const c = input[i];
    if (/\s/.test(c)) {
      i++;
      continue;
    }
    if (i + 1 < input.length && TWO_CHAR_OPS.has(input.slice(i, i + 2))) {
      tokens.push({ type: "op", value: input.slice(i, i + 2) });
      i += 2;
      continue;
    }
    if (c === "<" || c === ">") {
      tokens.push({ type: "op", value: c });
      i++;
      continue;
    }
    if ("()[],.".includes(c)) {
      tokens.push({ type: "punct", value: c });
      i++;
      continue;
    }
    if (c === '"') {
      let j = i + 1;
      let val = "";
      while (j < input.length && input[j] !== '"') {
        if (input[j] === "\\" && j + 1 < input.length) {
          val += input[j + 1];
          j += 2;
        } else {
          val += input[j];
          j++;
        }
      }
      tokens.push({ type: "string", value: val });
      i = j + 1;
      continue;
    }
    if (/\d/.test(c) || (c === "-" && /\d/.test(input[i + 1] ?? ""))) {
      let j = i + (c === "-" ? 1 : 0);
      while (j < input.length && /[\d.]/.test(input[j])) j++;
      tokens.push({ type: "number", value: parseFloat(input.slice(i, j)) });
      i = j;
      continue;
    }
    if (/[a-zA-Z_]/.test(c)) {
      let j = i;
      while (j < input.length && /\w/.test(input[j])) j++;
      const word = input.slice(i, j);
      if (KEYWORDS.has(word)) {
        tokens.push({ type: "keyword", value: word as any });
      } else {
        tokens.push({ type: "ident", value: word });
      }
      i = j;
      continue;
    }
    i++;
  }
  return tokens;
}

function unescapeRegex(s: string): string {
  return s.replace(/\\(.)/g, "$1");
}

function mapOperator(op: string, value: any): string {
  switch (op) {
    case "==":
      return value === null ? "is_null" : "equals";
    case "!=":
      return value === null ? "is_not_null" : "not_equals";
    case ">":
      return "greater_than";
    case "<":
      return "less_than";
    case ">=":
      return "greater_than_equal";
    case "<=":
      return "less_than_equal";
    case "=~": {
      const s = String(value);
      if (s.startsWith("^")) return "starts_with";
      if (s.endsWith("$")) return "ends_with";
      return "contains";
    }
    case "!~":
      return "not_contains";
    default:
      return "equals";
  }
}

class Parser {
  pos = 0;
  constructor(public tokens: Token[]) {}

  peek(): Token | undefined {
    return this.tokens[this.pos];
  }
  consume(): Token {
    return this.tokens[this.pos++];
  }

  parseOr(): Rule | RuleGroup {
    const first = this.parseAnd();
    const rules: (Rule | RuleGroup)[] = [first];
    while (this.peek()?.type === "keyword" && (this.peek() as any).value === "or") {
      this.consume();
      rules.push(this.parseAnd());
    }
    if (rules.length === 1) return first;
    return { id: uuidv4(), combinator: "OR", rules };
  }

  parseAnd(): Rule | RuleGroup {
    const first = this.parsePrimary();
    const rules: (Rule | RuleGroup)[] = [first];
    while (this.peek()?.type === "keyword" && (this.peek() as any).value === "and") {
      this.consume();
      rules.push(this.parsePrimary());
    }
    if (rules.length === 1) return first;
    return { id: uuidv4(), combinator: "AND", rules };
  }

  parsePrimary(): Rule | RuleGroup {
    const tok = this.peek();
    if (tok?.type === "punct" && tok.value === "(") {
      this.consume();
      const inner = this.parseOr();
      if (this.peek()?.type === "punct" && (this.peek() as any).value === ")") this.consume();
      return inner;
    }
    return this.parseComparison();
  }

  parseComparison(): Rule {
    const entityTok = this.consume();
    if (!entityTok || entityTok.type !== "ident") {
      throw new Error("expected Entity.field");
    }
    const entity = entityTok.value;

    // Collect the dot-separated path after the entity. A single segment is a
    // plain field (`status`); multiple segments are FK traversal
    // (`academic_period.code`) which the schema represents with Django `__`
    // notation, so re-join with `__` to match the field dropdown value.
    const fieldParts: string[] = [];
    while (this.peek()?.type === "punct" && (this.peek() as any).value === ".") {
      this.consume();
      const part = this.consume();
      if (!part || part.type !== "ident") throw new Error("expected field segment");
      fieldParts.push(part.value);
    }
    if (fieldParts.length === 0) throw new Error("expected Entity.field");
    const field = fieldParts.join("__");

    const opTok = this.consume();
    if (!opTok) throw new Error("expected operator");

    if (opTok.type === "keyword" && opTok.value === "in") {
      // `in value_group.<code>`: a catalog group, kept by code (MPN-2328).
      if (this.peek()?.type === "ident" && (this.peek() as any).value === "value_group") {
        this.consume();
        const dot = this.consume();
        if (!dot || dot.type !== "punct" || (dot as any).value !== ".") throw new Error("expected value_group.<code>");
        const code = this.consume();
        if (!code || code.type !== "ident") throw new Error("expected value_group.<code>");
        return { id: uuidv4(), entity, field, operator: "in", value: null, value_group: code.value };
      }
      if (this.peek()?.type === "punct" && (this.peek() as any).value === "[") this.consume();
      const values: any[] = [];
      while (this.peek() && !(this.peek()!.type === "punct" && (this.peek() as any).value === "]")) {
        values.push(this.parseValue());
        if (this.peek()?.type === "punct" && (this.peek() as any).value === ",") this.consume();
      }
      if (this.peek()?.type === "punct" && (this.peek() as any).value === "]") this.consume();
      return { id: uuidv4(), entity, field, operator: "in", value: values };
    }

    const opStr = (opTok as any).value;
    const value = this.parseValue();
    const operator = mapOperator(opStr, value);

    let resolvedValue: any = value;
    if (opStr === "=~" || opStr === "!~") {
      const s = String(value);
      if (operator === "starts_with") resolvedValue = unescapeRegex(s.slice(1));
      else if (operator === "ends_with") resolvedValue = unescapeRegex(s.slice(0, -1));
      else resolvedValue = unescapeRegex(s);
    }
    return { id: uuidv4(), entity, field, operator, value: resolvedValue };
  }

  parseValue(): any {
    // Both failure paths THROW rather than yielding null. Returning null for a
    // missing or unrecognized token turned truncated text into a different,
    // valid-looking rule: `student.is_registered ==` parsed as
    // `student.is_registered == null` (the is_null operator), which the builder
    // then showed and re-saved as a rule nobody wrote. A real `== null` arrives
    // as the `null` KEYWORD, so it is still parsed below and unaffected.
    const tok = this.consume();
    if (!tok) throw new Error("expected a value");
    if (tok.type === "string") return tok.value;
    if (tok.type === "number") return tok.value;
    if (tok.type === "keyword") {
      if (tok.value === "true") return true;
      if (tok.value === "false") return false;
      if (tok.value === "null") return null;
    }
    throw new Error(`expected a value, got ${tok.type} ${String((tok as any).value)}`);
  }
}

// A dimensioned condition compiles to `<field clause> and dimension.<key> == "<val>"`
// (compileDimension). The generic parser reads the trailing `dimension.<key> == ...`
// as its own comparison with entity "dimension"; fold those back onto the
// preceding real condition's `.dimension` so a saved rule reappears in the
// builder as one row with its dimension picker set (MPN-1202). An inline
// `and dimension...` inside an OR also promotes its clause to a nested AND group
// on parse, so collapse the resulting single-rule groups back into their parent
// (compile already parenthesizes subgroups, so single-rule groups never survive
// a round-trip anyway — this keeps that invariant).
function foldDimensions(group: RuleGroup): RuleGroup {
  const rules: (Rule | RuleGroup)[] = [];
  for (const node of group.rules) {
    if ("combinator" in node) {
      const folded = foldDimensions(node as RuleGroup);
      if (folded.rules.length === 1) rules.push(folded.rules[0]);
      else rules.push(folded);
      continue;
    }
    const rule = node as Rule;
    if (rule.entity === "dimension") {
      const prev = rules[rules.length - 1];
      if (prev && !("combinator" in prev)) {
        (prev as Rule).dimension = { ...((prev as Rule).dimension ?? {}), [rule.field]: rule.value };
      }
      // An orphan dimension clause with no preceding condition is dropped — it
      // can't bind to anything and never comes from the builder.
      continue;
    }
    rules.push({ ...rule });
  }
  return { ...group, rules };
}

/**
 * Parse rule text into a builder group, or null when the text cannot be parsed.
 *
 * Callers that display or edit a rule MUST distinguish these: a failed parse
 * used to come back as an empty group, and because the modal recompiles the
 * group on every builder interaction and saves the result, one click on a rule
 * this parser could not read replaced it with "" — which the backend
 * (CmdSyncPricesheetItemConversionRule) treats as "clear the rule", silently
 * un-billing that conversion step (MPN-1529).
 *
 * Empty input is not a failure: it parses to an empty group.
 */
export function tryParseRuleText(text: string): RuleGroup | null {
  const empty: RuleGroup = { id: "root", combinator: "AND", rules: [] };
  if (!text || !text.trim()) return empty;
  try {
    const tokens = tokenize(text);
    if (tokens.length === 0) return null;
    const ast = new Parser(tokens).parseOr();
    const root: RuleGroup =
      "combinator" in ast
        ? { ...(ast as RuleGroup), id: "root" }
        : { id: "root", combinator: "AND", rules: [ast] };
    const folded = foldDimensions(root);
    // A non-empty rule that folds away to nothing is also a failure: it would
    // recompile to "" and wipe the rule just as a thrown parse error would.
    return folded.rules.length > 0 ? folded : null;
  } catch {
    return null;
  }
}

/**
 * Lenient wrapper: an unparseable rule comes back as an empty group.
 * Only for read-only affordances (e.g. counting conditions for a badge).
 * Anything that can SAVE the result must use tryParseRuleText.
 */
export function parseRuleText(text: string): RuleGroup {
  return tryParseRuleText(text) ?? { id: "root", combinator: "AND", rules: [] };
}
